import { Clip, ProjectType, Caption } from "@/types/type";
import { SessionContextValue } from "next-auth/react";
import { removeBase64InProject } from "@/lib/project-utils";
import axios from "axios";
import { MODAL_NAME } from "@/components/editor/stripe/constants";
import { getPreTranslatedProjectsByFolderId, translationProject } from "./translation-utils";
import { timeSelector } from "./text-utils";
import { extractPlanType } from "@/lib/export-plan-type-utils";
import { runtimeEnv } from "@/lib/runtime-utils";
import { StreamScene } from "./automation/automation-stream-scene";
import { cloneDeep } from "lodash";
import { preproc1Subtitle, preproc2Subtitle } from "@/hooks/useSubtitleHooks";
import {
  normalizeClipId,
  normalizeClipLayer,
  migrateClipTrim,
  normalizeTextStyle,
} from "@/lib/normalize-utils";

import {
  setAfterPreprocessMyAvatars,
  getBeforePreprocessMyAvatarsParams,
  setMyAvatarsMaskingOptions,
} from "@/hooks/useCheckMyAvatarStatus";
import { applyStream } from "@/hooks/automation/useAutomationStreaming";
import { ApiError } from "@/lib/api-exceptions";

import type { Scene } from "@/types/type";
import type { MyAvatarAsset } from "@/types/models";
import type { Db } from "mongodb";
import type {
  AutomationStreamingStream,
  AutomationStreamingMediaInfo,
} from "@/types/automation";

// hooks/useCheckEmptyScript.ts
export const whenExportCheckEmptyScript = (project:ProjectType) => {
  if (!project?.scenes) return;

    let sceneNumber = 1;
    for (const scene of project?.scenes) {
      const modelClip = scene.clips.find((c: Clip) => c.type === "aiModel");
      if(!scene.isDualAvatar && modelClip?.isDelete) continue; //single model 이면서 isDelete: true 인경우 skip
      let scriptIndex = 0;
      for (const script of scene.scripts) {
        scriptIndex++;
        if(!scene.isDualAvatar && scriptIndex > 1) continue;
        if(scene.isDualAvatar && !script.modelId) continue; //dualAvatar tab 선택시 미리 만들어 두고 모델을 선택하지 않은 스크립트는 제외
        const cleanedText = script?.org.replace(/<[^>]*>/g, "");
        if ((!script || !cleanedText) && !script?.tts?.url) {    
          return sceneNumber;
        }
      }
      sceneNumber++;
    }
    return;
}
// hooks/editor-hooks.ts
export const whenExportCheckScriptTextLength = (script: string): number => {
  script = script || "";
  const string = script.replace(/<\/?[^>]+(>|$)/g, "");
  return string ? string.length : 0
};

const whenExportGetShutterStockProductLicensed = async (params: {
  id: string; type: string;
}) => {
  try {
    await axios.post(
      runtimeEnv.BK_STUDIO_URL+"/api/project/media/license",
      {
        id: params.id,
        type: params.type,
        provider: "shutterstock",
      }
    ) 
  } catch (e) {
    console.error(e);
  }
}

export interface ExportProjectInput {
  // defaultErrorHandler: (error:any) => Promise<void>,

  projectId: string,
  project: ProjectType,    
  projects? : ProjectType[],
  creditInfo: any,//creditInfo.automationCredit
  session: SessionContextValue,
  cookies: any, // import { useCookies } from "react-cookie";
  router: any,//router.query
  userInfo:any,
  userId?: string,
  t: any,
  aiModels: any, 
  selectedLanguages: any[],
  onlyTranslateScript:boolean,
  ttsList?: any[],
  exportType:string,
  templateData:any,
  userSubscription:any,
  aiModelScripts: any, //선택된 씬의 scripts
  automationJobId?: string, 
  db?: any, // mognodb client
  ObjectId?: any, // mognodb client
  headers?: any, // user api header session
  sendInfoList?: any; // 대량발송 관련
  directSend?: boolean; // 대량발송 관련
  useCaption?: boolean; // 대량발송 관련
  isFromServerside : boolean;
  isCustomAvatar : boolean;
  workspaceId: string,
  /**
   * 대량 발송 관련일 때 사용
   * 또는 번역하여 내보내기 시 사용
   * 
   * - 번역하여 내보내기 시
   *   - 프로젝트를 폴더 내에 포함시키도록 처리함
   */
  folderId?: string; // 대량발송 관련
  /**
   * 번역하여 내보내기 시 원본 프로젝트는 익스포트 하지 않도록 하는 옵션
   * @default false
   */
  skipOriginalProjectWhenTranslate?: boolean;
  baseInfoId?: any; // 대량발송 관련
  myAvatars?: MyAvatarAsset[];
  audioDownloadUrl?: string;
  captionTranslationLanguage?: string | null;
}

interface ExportProgram {
  do: (...args: any[]) => Promise<{isSucceed:boolean, data?:any}>;
  onBeforeRun?: (...args: any) => Promise<any>;
  onCompleted?: (...args: any) => Promise<any>;
  onFailed?: (...args: any) => Promise<any>;
  onError?: (error:Error) => Promise<any>;
}

export enum ExportProgramName {
  auth = "_auth", 
  login = "_login", 
  emptyScript = "_emptyScript", 
  scriptTextLength = "_scriptTextLength", 
  textModeration = "_textModeration", 
  myAvatarPreprocess = "_myAvatarPreprocess",
  saveMyAvatarPreprocess = "_saveMyAvatarPreprocess",
  checkTemplateMyAvatar = "_checkTemplateMyAvatar",
  shutterStock = "_shutterStock",
  cleanProject = "_cleanProject",
  translateProjects = "_translateProjects",
  export = "_export",
  exportEarlyPost = "_exportEarlyPost",
  automationResult = "_automationResult",
  buildProjectFromAutomation = "_buildProjectFromAutomation",
  buildAudioCaption = "_buildAudioCaption",
  buildCaptionOnly = "_buildCaptionOnly",
  exportMultipleSend = "_exportMultipleSend",
  exportBulkVideo = "_exportBulkVideo",
  validateAvatar = "_validateAvatar",
  normalizeClip = "_normalizeClip",
  restoreCaptionTime = "_restoreCaptionTime",
  checkInvalidVoice = "_checkInvalidVoice",
  exportEarlyStreaming = "_exportEarlyStreaming",
}

export class ExportProject {    
  private readonly input: Partial<ExportProjectInput>;

  private projectId?: string;

  private project?: ProjectType; // deep copied !!

  private projects?: ProjectType[] = [];

  private router: any;

  private session?: SessionContextValue;

  private cookies: any;

  private userInfo: any;

  private userId?: string;

  private creditInfo: any;

  private t: any;

  private aiModels: any;

  private aiModelScripts: any;    

  private selectedLanguages: any[];

  private onlyTranslateScript:boolean

  private ttsList?: any[];

  private exportType:string;

  private templateData: any;

  private userSubscription: any;

  private time?: number;

  private isGPT: boolean;

  private authenticated?: boolean;

  private isAutomation?: boolean;

  private automationJobId?: string;

  private automationJobResult?: any;

  private db?: Db;

  private ObjectId?: any;

  private headers?: any;

  private temp = false;

  private sendInfoList?: any;

  private directSend?: boolean;

  private useCaption?: boolean;

  private isFromServerside? = false;

  private isCustomAvatar? = false;

  private workspaceId?: string;

  private audioDownloadUrl? = '';

  /**
   * 대량 발송 관련일 때 사용
   * 또는 번역하여 내보내기 시 사용
   * 
   * - 번역하여 내보내기 시
   *   - 프로젝트를 폴더 내에 포함시키도록 처리함
   */
  private folderId?: string;

  /**
   * 번역하여 내보내기 시 원본 프로젝트는 익스포트 하지 않도록 하는 옵션
   * @default false
   */
  private skipOriginalProjectWhenTranslate?: boolean;

  private baseInfoId?: any;

  private myAvatars?: MyAvatarAsset[];

  private captionTranslationLanguage?: string|null = null;

  constructor(input: Partial<ExportProjectInput>) {
      this.input = cloneDeep(input);
      this.projectId = input.projectId;
      this.session = input.session;
      this.cookies = input.cookies;
      this.router = input.router;
      this.userInfo = input.userInfo;
      this.userId = input.userId;
      this.creditInfo = input.creditInfo;
      this.t = input.t;
      this.aiModels = input.aiModels;
      this.selectedLanguages = input.selectedLanguages || [];
      this.onlyTranslateScript = input.onlyTranslateScript || false;
      this.ttsList = input.ttsList;
      this.exportType = input.exportType || "video";
      this.templateData = input.templateData;
      this.userSubscription = input.userSubscription;
      this.automationJobId = input.automationJobId;
      this.db = input.db; 
      this.ObjectId = input.ObjectId; 
      this.headers = input.headers; 
      this.sendInfoList = input.sendInfoList;
      this.directSend = input.directSend;
      this.useCaption = input.useCaption;
      this.isFromServerside = input.isFromServerside;
      this.isCustomAvatar = input.isCustomAvatar;
      this.folderId = input.folderId;
      this.skipOriginalProjectWhenTranslate = input.skipOriginalProjectWhenTranslate || false;
      this.baseInfoId = input.baseInfoId;
      this.workspaceId = input.workspaceId;
      this.myAvatars = input.myAvatars;
      this.audioDownloadUrl = input.audioDownloadUrl || '';
      if (input.project) {
        this.project = JSON.parse(JSON.stringify(input.project));
        this.time = timeSelector(this.project);
      }

      this.isGPT = input.router?.query.isGPT === 'true' ? true : false;
      this.isAutomation = input.router?.query?.type === "automation";
      if (this.automationJobId) {
        this.isAutomation = true;
      }

      this.projects = input.projects || [];
      this.captionTranslationLanguage = input.captionTranslationLanguage || null;
      console.log('[EP: input', input);
  }

  runEarlyPre = async (programOrder?: ExportProgramName[]) => {
    programOrder = programOrder || [
      ExportProgramName.auth, 
      ExportProgramName.login, 
      ExportProgramName.exportEarlyPost 
    ];

    return this.run(programOrder);
  }

  runEarlyPost = async (programOrder?: ExportProgramName[]) => {
    programOrder = programOrder || [
      ExportProgramName.automationResult, 
      ExportProgramName.buildProjectFromAutomation, 
      ExportProgramName.buildAudioCaption, 
      ExportProgramName.scriptTextLength, 
      ExportProgramName.myAvatarPreprocess,
      ExportProgramName.shutterStock,
      ExportProgramName.cleanProject, 
      ExportProgramName.translateProjects, 
      ExportProgramName.export, 
    ];

    return this.run(programOrder);
  }

  runAuthPre = async (programOrder?: ExportProgramName[]) => {
    programOrder = programOrder || [
      ExportProgramName.auth,
      ExportProgramName.login,
    ];

    return this.run(programOrder);
  }

  runMultipleSendPre = async (programOrder?: ExportProgramName[]) => {
    programOrder = programOrder || [
      ExportProgramName.myAvatarPreprocess,
      ExportProgramName.exportMultipleSend,
    ];

    return this.run(programOrder);
  }

  runBulkVideoPre = async (programOrder?: ExportProgramName[]) => {
    programOrder = programOrder || [
      ExportProgramName.myAvatarPreprocess,
      ExportProgramName.exportBulkVideo,
    ];

    return this.run(programOrder);
  }

  runAuthPass = async (programOrder?: ExportProgramName[]) => {
    programOrder = programOrder || [
      ExportProgramName.emptyScript, 
      ExportProgramName.scriptTextLength, 
      ExportProgramName.textModeration, 
      ExportProgramName.myAvatarPreprocess,
      ExportProgramName.shutterStock,
      ExportProgramName.cleanProject, 
      ExportProgramName.translateProjects, 
      ExportProgramName.export, 
    ];

    return this.run(programOrder);
  }

  runTemplatePreview = async (programOrder?: ExportProgramName[]) => {
    programOrder = programOrder || [
      // ExportProgramName.emptyScript,
      // ExportProgramName.scriptTextLength,
      ExportProgramName.validateAvatar,
      ExportProgramName.normalizeClip,
      ExportProgramName.restoreCaptionTime,
      ExportProgramName.checkTemplateMyAvatar,
      ExportProgramName.shutterStock,
      ExportProgramName.cleanProject,
    ];

    for (const programKey of programOrder) {
      const program: ExportProgram = this[programKey];
      try {
        if (!program) {
          throw new Error("The defined preprocessing program does not exist.");
        };

        const result = await program.do()

        if (!result.isSucceed) {
          throw new Error(`An error occurred while performing step ${programKey}.`);
        }
      } catch (e) {
        if (e instanceof Error && e.message) {
          return e;
        }
        return Error("An unknown error has occurred.")
      }
    }

    return this.project;
  }

  //에디터내에서 번역만 하는 경우 사용(내보내기는 하지 않고)
  runTranslateOnly = async (programOrder?: ExportProgramName[]) => {
    programOrder = programOrder || [
      ExportProgramName.cleanProject, 
      ExportProgramName.translateProjects, 
    ];

    return this.run(programOrder);
  }

  touchPreTranslatedProject = async (programOrder?: ExportProgramName[]) => {
    programOrder = programOrder || [
      ExportProgramName.emptyScript,
      ExportProgramName.scriptTextLength,
      ExportProgramName.textModeration,
      ExportProgramName.validateAvatar,
      ExportProgramName.normalizeClip,
      ExportProgramName.restoreCaptionTime,
      ExportProgramName.myAvatarPreprocess,
      ExportProgramName.shutterStock,
      ExportProgramName.cleanProject,
    ];

    let success = true;

    for (const programKey of programOrder) {
      const program: ExportProgram = this[programKey];

      try {
        if (!program) {
          throw new Error();
        };
                
        if (program.onBeforeRun) {
          await program.onBeforeRun();
        }
        
        const result = await program.do();

        if (!result.isSucceed) {
          throw new Error();
        }

        if (program.onCompleted) {
          await program.onCompleted(result.data);
        }
      } catch (error) {
        console.error(`[EP:touchPreTranslatedProject:${programKey}] => onError`);
        success = false;
        break;
      }
    }

    if (!success) {
      return null;
    }

    return this.project;
  }

  run = async (programOrder?: ExportProgramName[]) => {
    // 익스포트 실행순서 정의
    programOrder = programOrder || [
      ExportProgramName.auth, 
      ExportProgramName.emptyScript, 
      ExportProgramName.scriptTextLength, 
      ExportProgramName.checkInvalidVoice,
      ExportProgramName.textModeration, 
      ExportProgramName.validateAvatar,
      ExportProgramName.normalizeClip,
      ExportProgramName.restoreCaptionTime,
      ExportProgramName.myAvatarPreprocess,
      ExportProgramName.shutterStock,
      ExportProgramName.cleanProject, 
      ExportProgramName.translateProjects, 
      ExportProgramName.export, 
    ];

    for (const programKey of programOrder) {
      const program: ExportProgram = this[programKey];

      try {

        if (!program) throw(new Error('no export program ===>' + programKey));
                
        if (program.onBeforeRun) {
          // 1. onBefore가 있으면 실행전에 먼저 실행
          console.log(`[EP:${programKey}] => onBefore`)
          await program.onBeforeRun();
        }
        console.log(`[EP:${programKey}] => do`)
        // 2. 진짜 태스크 실행 !!
        const result = await program.do()
        if (result.isSucceed && program.onCompleted) {
          console.log(`[EP:${programKey}] => onComplted`);
          await program.onCompleted(result.data);
        }
        else if (!result.isSucceed) {
          if (program.onFailed) {
            // 3. 실패했는데 onFailed가 있으면 실행하고 끝냄
            console.log(`[EP:${programKey}] => onFailed`)
            await program.onFailed(result.data);
          } else {
            // 4. 실패했는데 onFailed 가 없으면 error로 보냄
            throw (new Error());
          }
          break;
        } 
        
      } catch (error) {
        // 정말 exception 에러가 나오거나, 또는 failed 상태인데, onFailed가 없으면 onError로 떨어짐
        if (program.onError) {
          // 5. onError가 있으면 실행
          console.error(`[EP:${programKey}] => onError`)
          await program.onError(error as Error);
        }
        else {
          // 6. 최후의 방어선 defaultErrorHandler 
          console.error(`[EP:${programKey}] => defaultErrorHandler`, error)
          this.defaultErrorHandler(new Error(programKey));
        }
        break;
      }
    }
  }

  runWithoutExport = async (programOrder: ExportProgramName[]) => {
    if (!this.project) {
      return null;
    }
    
    for (const programKey of programOrder) {
      const program: ExportProgram = this[programKey];
      try {
        if (!program) {
          throw new Error("The defined preprocessing program does not exist.");
        };

        const result = await program.do()

        if (!result.isSucceed) {
          throw new Error(`An error occurred while performing step ${programKey}.`);
        }
      } catch (e) {
        console.error(e);
        return null;
      }
    }

    return this.project;
  }

  saveDbProejct = async()=> {
    // 몇몇 백엔드 api는 proejctId만 넘겨받아서 수행되는 api가 있다. 따라서 가급적 단위별 업데이트 후 DB도 저장.
    if (!this.project) return;

    const {db, ObjectId, project} = this;

    if (project?.workspaceId && project.workspaceId.length > 0) {
      for (const [idx, value] of (this.project.workspaceId || []).entries()) {
        if (typeof value === 'string') project.workspaceId[idx] = new ObjectId(value);
      }
    }

    if (db) {
      const updateData:any = {
        automation : project.automation,
        workspaceId : project.workspaceId,
        scenes : project.scenes,
        orientation : project.orientation,
        templateId : project.templateId,
        userType : project.userType,
        dictionary : project.dictionary,
        preprocess : project.preprocess
      }
      if (project.state === 'error') {
        updateData.state = project.state;
        updateData.error = project.error;
      }

      await db.collection('ai-video-jobs').updateOne({_id:new ObjectId(project._id)}, {$set:updateData});
      console.log('saveDbProejct complete !!', project);
    }
  }

  getDbProject = async() => {
    const {db, ObjectId, project} = this;
    if (db && project) {
      const result = await db.collection('ai-video-jobs').findOne({_id:new ObjectId(project._id)});
      return result;
    }
  }

  errorDbUpdate = async (code:number, msg:string) => {
    if (!this.project) return;
    this.project.state = 'error';
    this.project.error = {code, msg};
    await this.saveDbProejct();
    return;    
  }

  private defaultErrorHandler = async function (this:any, error:Error) {
    console.error('ExportProject::DefaultError =>', error);
    this.temp = true;
  }

  setDefaultErrorHandler = (handler:(error:any) => Promise<void>) => {
    this.defaultErrorHandler = handler;
  }

  onBefore(functionName:ExportProgramName, method:(...args: any[]) => Promise<void>) {
    this[functionName].onBeforeRun = method;
  }

  onFailed(functionName:ExportProgramName, method:(...args: any[]) => Promise<void>) {
    this[functionName].onFailed = method;
  }
  
  onCompleted(functionName:ExportProgramName, method:(...args: any[]) => Promise<void>) {
    this[functionName].onCompleted = method;
  }

  onError(functionName:ExportProgramName, method:(...args: any[]) => Promise<void>) {
    this[functionName].onError = method;
  }


  /*
    내보내기 단위별 정의
  */

  [ExportProgramName.auth]:ExportProgram = {
    do: async () => {
      const {session, authenticated, cookies, creditInfo, isAutomation, templateData, selectedLanguages, aiModels, time, project, userSubscription, userInfo, t} = this;

      this.authenticated = session?.status === 'authenticated';
      const loginStatus = this.authenticated ? 'log_in' : 'log_off';
      if (window?.dataLayer?.push) {
        window.dataLayer.push({ event: "Video_Generate", status_login: loginStatus });
      }

      // // console.log('기간 userInfo?.company?.expired: ', userInfo?.company?.expired, '  dayjs() ',dayjs().isAfter(dayjs("2024-03-06T14:59:59.000Z")), ' dayjs() ',dayjs() )
      // if(userInfo?.demo && userInfo?.company?.expired && dayjs().isAfter(dayjs(userInfo.company.expired))) {
      //   close();
      //   const noticeModalPayload = {
      //     currentPlan: 'Demo',
      //     openedModal: MODAL_NAME.NOTICE,
      //     modalTitle: t("Demo period expires"),
      //     modalLogoUrl: "/images/modal/material-symbols_folder-limited.png",
      //     noticeTitle: t("The validity period has expired."),
      //     noticeExplains: [t("If you would like to extend the validity period, please contact the administrator.")],
      //   };
      //   return {isSucceed:false, data:noticeModalPayload}
      // }

      if(creditInfo?.automationCredit === 0 && isAutomation && authenticated && cookies?.first_export){
        const noticeModalPayload = {
          currentPlan: userInfo?.plan,
          openedModal: MODAL_NAME.NOTICE,
          modalTitle: t("MAXIMUM LIMIT"),
          modalLogoUrl: "/images/modal/material-symbols_folder-limited.png",
          noticeTitle: t("You’ve reached the maximum limit"),
          noticeExplains: [t("By upgrading your plan, you can continue generating more videos using AI prompts.")
          ],
        };
        return {isSucceed:false, data:noticeModalPayload}
      }


      let aiAvatar = aiModels?.[0]?.model?.label?.en || aiModels?.[0]?.model?.ai_name || "";
      for (const scene of project?.scenes) {
        const modelClip = scene.clips.find((c: Clip) => c.type === "aiModel");
        console.log("modelClip!!!: ", modelClip);
        if (modelClip?.avatarType === "dreamAvatar") {
          aiAvatar = "dreamAvatar";
        }else if (modelClip?.avatarType === "customAvatar") {
          aiAvatar = "customAvatar";
        }
      }

      const languageEditor = selectedLanguages.join(',');
      const slides = project?.scenes?.length || 0;

      if (window?.dataLayer?.push) { 
        if(!cookies?.first_export){
          window.dataLayer.push({ 
            event: "first_export",
            template_category: templateData?.category || "",
            template_id: templateData?.templateId || "",
            language_editor: languageEditor,
            avatar_name: aiAvatar,
            subscription: extractPlanType(userSubscription?.plan),
            duration: time,
            slides,
            location : isAutomation ? "automation" : "editor",
          });
        }
        
        window.dataLayer.push({ 
          event: "export",
          template_category: templateData?.category || "",
          template_id: templateData?.templateId || "",
          language_editor: languageEditor,
          avatar_name: aiAvatar,
          subscription: extractPlanType(userSubscription?.plan),
          duration: time,
          slides,
          location : isAutomation ? "automation" : "editor",
        });
      }

      return {isSucceed:true}

    }
  };

  [ExportProgramName.login]:ExportProgram = {
    do: async () => {
      const {session} = this;
      if (session?.status !== 'authenticated') return {isSucceed:false}
      return {isSucceed:true}
    }
  };

  [ExportProgramName.emptyScript]:ExportProgram = {
    do: async () => {
      if (!this.project) {
        return {isSucceed:false, data:0}
      }
      const errorSceneIdx = whenExportCheckEmptyScript(this.project);
      if (errorSceneIdx) return {isSucceed:false, data:errorSceneIdx}
      return {isSucceed:true}
    }
  };
  
  [ExportProgramName.scriptTextLength]:ExportProgram = {
    do: async () => {
      // const {aiModelScripts} = this;
      // const textLength = whenExportCheckScriptTextLength(aiModelScripts?.[0]?.org.replaceAll("\ufeff", ""));
      // if (textLength > 1000) {
      //   return {isSucceed:false}
      // }
      return {isSucceed:true}
    }
  };

  [ExportProgramName.myAvatarPreprocess]:ExportProgram = {
    do: async () => {
      if (this.myAvatars && this.project) {
        this.project = setAfterPreprocessMyAvatars({
          avatars: this.myAvatars, project: this.project,
        });
        this.project.preprocess = getBeforePreprocessMyAvatarsParams({
          avatars: this.myAvatars, project: this.project,
        });
        this.project = setMyAvatarsMaskingOptions({
          avatars: this.myAvatars, project: this.project,
        })
      }
      return {isSucceed:true}
    }
  };

  /**
   * myAvatar 전처리 정보 저장
   * 전처리 완료되지 않은 customAvatar 가 쓰일 경우 myAvatarPreprocess 에서 preprocess 라는 객체를 project 에 추가한다.
   * 그 후 ExportProgramName.export에서 따로 DB 에 write 하지 않고 바로 해당 프로젝트를 export 시킴. 따라서 export 로직을 ClassExport 에서 분리시켜 사용할 경우,
   * 이 전처리값을 DB 에 따로 write 해주어야 함. 4/18/25
   */
  [ExportProgramName.saveMyAvatarPreprocess]:ExportProgram = {
    do: async () => {
      if(this.project?.preprocess) {
        await this.saveDbProejct();
      }
      return {isSucceed:true}
    }
  };

  [ExportProgramName.checkTemplateMyAvatar]:ExportProgram = {
    do: async () => {
      if (this.project) {
        const check = this.project.scenes.some((scene) => {
          return scene.clips.some((clip) => {
            if (
              clip.type === "aiModel" &&
              (
                clip.avatarType === "dreamAvatar" ||
                clip.avatarType === "customAvatar"
              ) && 
              !clip.model.preprocess_path
            ) {
              return true;
            }
            return false;
          });
        });

        if (check) {
          return { isSucceed: false };
        }
      }
      return { isSucceed: true };
    }
  };

  [ExportProgramName.shutterStock]:ExportProgram = {
    do: async () => {
      try {
        const { project } = this;
  
        if (!project) {
          throw new Error("project is undefined");
        }
  
        for (const [, scene] of project.scenes.entries()) {
          for (const clip of scene.clips) {
            if (clip.type == "aiModel") {
              // 모델의 original voice 일 때 tts 정보를 삭제하고 POST
              if (clip.model.ai_name === clip.script?.tts?.model) delete clip.script?.tts;
            }
            if (clip?.from?.toLowerCase() === "shutterstock") {
              // getProductLicense
              const id = clip.shutterStockId;
              const type = (() => {
                if (clip.type === "image") {
                  return "image";
                }
                if (clip.type === "videoImage") {
                  return "video";
                }
                if (clip.type === "audio") {
                  return "audio";
                }
                return null;
              })();
  
              if (!id || !type) {
                continue;
              }
  
              await whenExportGetShutterStockProductLicensed({ id, type });
  
              if (
                clip?.from?.toLowerCase() === "shutterstock" &&
                type === "video" &&
                clip.licensedUrl
              ) {
                clip.video_url = clip.licensedUrl;
              }
            }
          }
        }
        return { isSucceed: true }
      } catch (e) {
        console.error(e);
        return { isSucceed: false }
      }
    }
  };

  [ExportProgramName.cleanProject]:ExportProgram = {
    do: async () => {
      this.project = removeBase64InProject(this.project);
      return {isSucceed:true}
    }
  };

  [ExportProgramName.translateProjects]:ExportProgram = {
    do: async () => {
      const {
        selectedLanguages,
        projects,
        project,
        onlyTranslateScript,
        ttsList,
        folderId,
      } = this;

      const replaceVoices = ["en", "ko", "zh", "ja", "es", "ru", "fr", "pt", "de", "it", "ar"].reduce((acc: any, cur: string) => {
        if(!ttsList || ttsList.length <= 0) return acc
        for(const g of ["MALE", "FEMALE"]) {
          const repLanguages:{[key:string]:string} = {
            en: "en-US",
            fr: "fr-FR",
            pt: "pt-PT",
            zh: "cmn-CN"
          }
          acc.push(ttsList?.find((e: any) => e.gender === g && (!!repLanguages[cur] ? e.language === repLanguages[cur] : e.language.startsWith(cur))));
        }
        return acc
      }, [])
      
      /**
       * 번역할 때
       */
      if(selectedLanguages.length > 0) {
        const preTranslatedProjects = await getPreTranslatedProjectsByFolderId(folderId);
        for(let i = 0; i < selectedLanguages.length; i++) {
          const existsProject = preTranslatedProjects.find((p) => p.translateTo === selectedLanguages[i])
          if (existsProject) {
            const childEp = new ExportProject({
              ...this.input,
              project: {
                ...existsProject,
                resolution: project?.resolution
              },
              projectId: existsProject._id,
            });

            const touchedProject = await childEp.touchPreTranslatedProject();

            if (!touchedProject) {
              return { isSucceed: false, data: selectedLanguages[i] };
            }

            projects?.push(touchedProject);
          } else {
            projects.push(await translationProject(
              project,
              selectedLanguages[i],
              replaceVoices,
              onlyTranslateScript,
              folderId,
            ));
          }
        }

        /**
         * 원본 프로젝트도 내보내기 할 것인지 판단
         */
        if (!this.skipOriginalProjectWhenTranslate) {
          projects.push(project)
        }
      }
      /**
       * 번역하지 않을 때\
       * 원본 프로젝트는 항상 내보내기
       */
      else {
        projects.push(project);
      }

      return {isSucceed:true};
    }
  };

  [ExportProgramName.export]:ExportProgram = {
    do: async () => {
      const {isAutomation, exportType, time, isGPT, headers} = this;
      let projects = this.projects;
      if ((!projects || projects.length === 0) && this.project) projects = [this.project];
      

      const responses = await projects.reduce(async (acc: any, curProject: any) => {
        const results = await acc.then()
        const data = { ...curProject, exportType, time, isGPT, isAutomation };
        // console.log(`[EP:${ExportProgramName.export}] req =>`, data);
        // console.log(`[EP:${ExportProgramName.export}] req headres =>`, headers);
        let res;
        
        res = (await axios.post(`${runtimeEnv.BK_STUDIO_URL}/api/project/${data._id}/export`, data, {headers: {
          cookie: this.headers?.cookie
        }})).data;

        console.log('[EP:_export] res =>', res);
        console.log('[EP:_export] cookie =>', this.headers);
        
        
        // console.log(`[EP:${ExportProgramName.export}] result =>`, res);
        return [...results, res]
      }, Promise.resolve([]))

      if (responses.every((res: any) => res.success)) return {isSucceed:true, data:responses};
 
      const errorResponse = responses.find((res: any) => !res.success && res.error?.code)
      if (errorResponse?.error?.code) return {isSucceed:false, data:errorResponse}
      throw(new Error('export error'));
    }
  };

  [ExportProgramName.automationResult]:ExportProgram = {
    do: async () => {
      const {automationJobId} = this;
      const that = this;

      if (!automationJobId) return {isSucceed:true};

      axios.get(runtimeEnv.BK_STUDIO_URL+"/api/dashboard/automationStream", {params: {automationJobId}});

      return new Promise((resolve, reject)=>{
        function check(timeSec:number):any {
          return setTimeout(async ()=>{
            const { data } = await axios.get(runtimeEnv.AUTOMATION_SERVER_URL+"/data/stream/"+automationJobId);

            for (const stream of data) {
              if (stream.jobStatus) {
                if (stream.jobStatus === 'finished') {
                  that.automationJobResult = data;
                  console.log(`[EP:${ExportProgramName.automationResult}] complete =>`, data.length);
                  return resolve({isSucceed:true});
                }
                return reject('error');
              }
            }
            return check(10);

          }, timeSec * 1000)
        };

        return check(15);
      })
    }
  };

  [ExportProgramName.buildProjectFromAutomation]:ExportProgram = {
    do: async () => {
      const {automationJobId, automationJobResult, templateData, workspaceId, ObjectId, session} = this;
      if (!automationJobId) return {isSucceed:true};

      let ttsList = this.ttsList;

      if (!ttsList || ttsList.length === 0) {
        try {
          /**
           * 이 파일은 클라이언트/서버 모두에서 사용되므로\
           * 클라이언트/서버 환경에 따라 다르게 동작하도록 처리함
           */
          if (typeof window === 'undefined') {
            /**
             * 임포트를 동적으로 하지 않으면 클라이언트에서 이 파일을 임포트 하면\
             * 모듈 오류가 발생함\
             * (클라이언트에서 서버 모듈을 사용한 것으로 취급되어 오류 발생)
             */
            const { findModelVoicesWithoutMongoId } = await import(
              '@/lib/model/voices'
            )
            ttsList = await findModelVoicesWithoutMongoId()
          } else {
            const { data: ttsRes } = await axios.get(
              `${runtimeEnv.BK_STUDIO_URL}/api/model/voices`,
            )

            if (ttsRes.success) {
              ttsList = ttsRes.data
            }
          }
        } catch (e) {
          console.error(e)
          // do nothing
        }
      }

      // stream 합치기
      const project = {
        ...cloneDeep(templateData),
        scenes: [],
      };
      for (const stream of automationJobResult) {
        if (stream.sceneIdx === undefined) continue;

        const sceneIdx = stream.sceneIdx;
        const scene = new StreamScene(stream, project, templateData, ttsList, this.isCustomAvatar).getScene();
        if (scene !== null) {
          project.scenes[sceneIdx] = scene;
        }
      }
    
      this.project = this.project || project;
      if (this.project) {
        this.project.scenes = project.scenes;
        if (this.projectId) {
          this.project._id = this.projectId;
        }
        this.project.automation = this.project.automation || { jobId:this.automationJobId };
        this.project.automation.isFinish = true;
        if(!this.project.userId) this.project.userId = session?.user?.id || this.userId;
        if (workspaceId) this.project.workspaceId = [new ObjectId(workspaceId)];
      }

      if(!this.time) this.time = timeSelector(this.project); // export without session 을 사용할때에는 automation build 하면서 scene 과 scripts 데이터가 결정나므로 바꾸어주어야 한다
      
      await this.saveDbProejct();

      return {isSucceed:true};
    }
  };

  [ExportProgramName.buildAudioCaption]:ExportProgram = {
    do: async () => {
      const {project, automationJobId, db, ObjectId, session} = this;
      if (!project || !automationJobId) return {isSucceed:true};
      
      project.automation.isBuildingAudio = true;
      await this.saveDbProejct();

      
      const projectId = project._id;
      
      // 오디오 생성
      project.scenes.forEach(scene=>preproc1Subtitle(scene));
      console.log("session: ", session)
      const { data:exportAudioRes } = await axios.post(runtimeEnv.BK_STUDIO_URL+"/api/project/exportAudio", {
        project: {
          ...project,
          _id: projectId,
          scenes: project.scenes.map((s) => {
            const { tempo, ...rest } = s; // 자막 요청 시 템포 무시
            return rest;
          })
        },
        sceneId: -1,
        isConcat: false,
        userId: session?.user?.id || this.userId
      });
      console.log('data', exportAudioRes);
      /**
       * NOTE: POST 요청자체가 실패할 경우 클라이언트에서 프로젝트를 로딩상태에 두지 않으려면 DB 에 별도의 오류처리가 필요하다. 
       * 이것을 exportAudio 엔드포인트 자체에서 처리할경우 미리듣기와 관련하여 사이드이펙드가 생길 수 있으므로 여기서 오류처리한다. 
       * 아래의 GET 요청 또한 마찬가지이다.
       */
      if(!exportAudioRes.success) {
        await db.collection('ai-video-jobs').updateOne({_id:new ObjectId(project._id)}, {$set:{
          state:'error',
          error: exportAudioRes.error
        }});
        return {isSucceed:false, data:"failed to process audio export post request"}
      }
      
      let audioUrl:any[];

      try {
        audioUrl = await (new Promise((resolve, reject)=>{
          let count = 0;
          let undefinedCount = 0;
          const check = async () => {
            const audioResponse = await axios.get(runtimeEnv.BK_STUDIO_URL+"/api/project/exportAudio", {
               params: { projectId, sceneId: -1 }
            });
  
            console.log('audio result', audioResponse?.data)

            /**
             * Note: exportAudio POST 요청이 제대로 이루어졌는데도 불구하고 exportAudio 필드가 바로 생기지 않는 경우가 있다 (MongoDB 부하 과다 의심).  
             * 따라서 exportAudioRes 가 존재하지 않을경우 바로 오류처리 하지않고 3번 기다리며 위와 같은 이유로 해당 프로젝트 또한 DB 에서 오류처리함
             */
            if (!audioResponse?.data) {
              undefinedCount++;
              if (undefinedCount >= 3) {
                await db.collection('ai-video-jobs').updateOne({_id:new ObjectId(project._id)}, {$set:{
                  state:'error',
                  error: audioResponse.data?.error || 'Unknown audio export error'
                }});
                reject(new Error("failed to process audio export get request"));
              }
              setTimeout(() => check(), 5000);
              return;
            }

            const audioData = audioResponse.data;
            const output = audioData?.data?.downloadUrls;
            if (output) return resolve(output);

            if(audioData.data?.state === 'error' && audioData.data?.error) {
              const error = audioData.data.error;
              await db.collection('ai-video-jobs').updateOne({_id:new ObjectId(project._id)}, {$set:{
                state:'error',
                error
              }});
              reject(new Error('audio export error'));
            }
            if (count++ > 240) { // 20분까지 오디오 생성이 안되면 타임아웃처리 처리 2024-10-16 Sage, Dewei 논의
              const error = {
                code: '9999',
                msg:'audio export timeout'
              };
              await db.collection('ai-video-jobs').updateOne({_id : new ObjectId(project._id)}, {$set: {
                state:'error',
                error
              }});
              reject(new Error('audio export timeout'));
            }
            setTimeout(() => check(), 5000);        
          }
          check();
        }));
      }
      catch( error ) {
        if(error instanceof Error) {
          return { isSucceed: false, data: error.message}
        }
        return { isSucceed: false, data: error}
      }
      

      console.log('audioUrl final result ===>', audioUrl)


      // 캡션 생성
      for (const [sceneId, scene] of project.scenes.entries()) {
        const captionClip = scene.clips.find((clip: Clip) => {
          return clip.type === "captions";
        });
        if (!captionClip) continue;

        let splitNum = Number(captionClip.tag?.match(/[0-9]+/) || 100);
        const downloadUrl = audioUrl?.[sceneId].output_audio_path;
        console.log('downloadUrl', downloadUrl)
        if (!downloadUrl) continue;
        
        const param = preproc2Subtitle(scene, {data:{downloadUrl}}, splitNum)
        const { data } = await axios.post(runtimeEnv.BK_STUDIO_URL+"/api/project/subtitle/wordTimestamp", param);        
        console.log('caption result ===>', data?.data);

        const tempo = Number(scene.tempo || 1);
        
        captionClip.captions = data?.data.map((item: Caption) => {
          return {
            ...item,
            start: Number((item.start / tempo).toFixed(2)),
            end: Number((item.end / tempo).toFixed(2)),
          }
        });
      }

      this.project = project
      if (this.project && this.project.automation) this.project.automation.isBuildingAudio = false;
      await this.saveDbProejct();

      return {isSucceed:true};
    }
  };

  [ExportProgramName.buildCaptionOnly]:ExportProgram = {
    do: async () => {
      const {projects, audioDownloadUrl, captionTranslationLanguage} = this;
      if (!projects || !audioDownloadUrl) return {isSucceed:true};

      const project = projects[0];

      // 캡션 생성
      for (const [sceneId, scene] of project.scenes.entries()) {
        const captionClip = scene.clips.find((clip: Clip) => {
          return clip.type === "captions";
        });
        if (!captionClip) continue;

        let splitNum = Number(captionClip.tag?.match(/[0-9]+/) || 100);
        const downloadUrl = audioDownloadUrl;
        if (!downloadUrl) continue;
        
        //const param = preproc2Subtitle(scene, {data:{downloadUrl}}, splitNum)
        
        const param = {
          url: downloadUrl,
          script: captionClip.text,
          langCode: 'en-US',
          splitNum: splitNum ?? 100,
        }

        console.log("WordStamp params", param);
        const { data } = await axios.post(runtimeEnv.BK_STUDIO_URL+"/api/project/subtitle/wordTimestamp", param);        
        console.log('caption result ===>', data?.data);

        const tempo = Number(scene.tempo || 1);
        
        captionClip.captions = data?.data.map((item: Caption) => {
          return {
            ...item,
            start: Number((item.start / tempo).toFixed(2)),
            end: Number((item.end / tempo).toFixed(2)),
          }
        });

        if(captionTranslationLanguage && captionClip.captions) {
          let textsToTranslate:string[] = [];
          captionClip.captions.forEach((item: Caption) => {
            textsToTranslate.push(item.caption);
          });

          const translationOutput = await fetch(`${runtimeEnv.BK_STUDIO_URL}/api/utils/translation`, {
            method: "PUT",
            body: JSON.stringify({text: textsToTranslate, target_lang: captionTranslationLanguage})
          })
          .then((res) =>res.json())
          .then(res => res.data.data);

          console.log("Translation Output: ", translationOutput);

          captionClip.captions.forEach((item: Caption, idx:number) => {
            item.caption = translationOutput[idx].text;
          });
        }
      }

      this.project = project
      this.projects = [project];
      if (this.project && this.project.automation) this.project.automation.isBuildingAudio = false;
      await this.saveDbProejct();

      return {isSucceed:true};
    }
  };

  // [ExportProgramName.exportEarlyPost]:ExportProgram = {
  //   do: async () => {
  //     const {automationJobId, session, templateData, exportType} = this;
      
  //     await axios.post(`${runtimeEnv.BK_STUDIO_URL}/api/automation/backend-export`, {
  //         automationJobId, 
  //         session, 
  //         projectId:this.project._id,
  //         templateData,
  //         exportType
  //       });

  //     return {isSucceed:true}
  //   }
  // };

  [ExportProgramName.exportEarlyPost]:ExportProgram = {
    do: async () => {
      const {automationJobId, session, templateData, exportType} = this;

      if (!this.project) {
        return {isSucceed:false}
      }
      
      await axios.post(`${runtimeEnv.BK_STUDIO_URL}/api/scheduler/createTask`, {
        type: 'exportEarly',
        data : {
          body: {
            automationJobId, 
            session, 
            projectId:this.project._id,
            templateData,
            exportType
          }
        }
      });

      await axios(`${runtimeEnv.BK_STUDIO_URL}/api/scheduler/handleScheduler`)

      return {isSucceed:true}
    }
  };

  [ExportProgramName.exportMultipleSend]: ExportProgram = {
    do: async () => {
      const { session, project, sendInfoList, directSend, folderId, workspaceId } = this;

      await axios.post(`${runtimeEnv.BK_STUDIO_URL}/api/scheduler/createTask`, {
        type: 'exportMultipleSend',
        data: {
          body: {
            session,
            project,
            sendInfoList,
            directSend,
            folderId,
            workspaceId,
          }
        }
      });

      axios(`${runtimeEnv.BK_STUDIO_URL}/api/scheduler/handleScheduler`);

      return { isSucceed: true }
    }
  };

  [ExportProgramName.exportBulkVideo]: ExportProgram = {
    do: async () => {
      const { session, project, sendInfoList, directSend, useCaption, folderId, workspaceId } = this;

      await axios.post(`${runtimeEnv.BK_STUDIO_URL}/api/scheduler/createTask`, {
        type: 'exportBulkVideo',
        data: {
          body: {
            session,
            project,
            sendInfoList,
            directSend,
            useCaption,
            folderId,
            workspaceId,
          }
        }
      });

      axios(`${runtimeEnv.BK_STUDIO_URL}/api/scheduler/handleScheduler`);

      return { isSucceed: true }
    }
  };

  [ExportProgramName.textModeration]: ExportProgram = {
    do: async () => {      

      try {
        const { project, userInfo } = this;
        if (userInfo?.company?.useTextModeration === false) return { isSucceed: true }
        if (userInfo?.useTextModeration === false) return { isSucceed: true }
  
        if (!project?.scenes) return { isSucceed: false }
  
        let sceneNumber = 1;
        for (const scene of project?.scenes) {
          const modelClip = scene.clips.find((c: Clip) => c.type === "aiModel");
          if(!scene.isDualAvatar && modelClip?.isDelete) continue; //single model 이면서 isDelete: true 인경우 skip
          let scriptIndex = 0;
          for (const script of scene.scripts) {
            scriptIndex++;
            if(!scene.isDualAvatar && scriptIndex > 1) continue;
            if(scene.isDualAvatar && !script.modelId) continue; 
            if(!script.org || !script.org.trim()) continue; 
  
            const cleanedText = script.org.replace(/<[^>]*>/g, " ");
            const language = script.tts?.language || script.detectedLanguage || script.modelSupportLanguage;
            let goAzure = true;
            // if (language.includes('en')) {
            //   goAzure = false;
            //   try {
            //     // console.log('textModeration openai !!!');
            //     const openaAiModeration = await axios.post(`${runtimeEnv.BK_STUDIO_URL}/api/dashboard/textModerate`, {openAiInput: cleanedText})
            //     // console.log('textModeration openai !!! result', openaAiModeration?.data);
            //     if (openaAiModeration?.data?.data?.flagged) {
            //       return {isSucceed:false, data: {sceneNumber}};
            //     }
            //     if (!openaAiModeration || !openaAiModeration.data) goAzure = true;
            //   } catch (error) {
            //     goAzure = true;
            //   }
            // }

            if (goAzure) {
              try {
                // console.log('textModeration azure !!!');
                const azureModeration = await axios.post(`${runtimeEnv.BK_STUDIO_URL}/api/dashboard/textModerate`, {azureInput: cleanedText})
                const azureResult = azureModeration?.data?.data?.categoriesAnalysis;
                if (azureResult) {
                  // console.log('textModeration azure result !!!', azureResult);
                  for (const item of azureResult) {
                    if (item.severity > 3) {
                      return {isSucceed:false, data: {sceneNumber}};
                    }
                  }
                }                
              } catch (error) {                
              }
            }            
          }
          sceneNumber++;
        }
  
        return { isSucceed: true }
        
      } catch (error) {
        console.error('textModeration error', error)
        return { isSucceed: true }
      }
    }
  };

  [ExportProgramName.validateAvatar]:ExportProgram = {
    do: async () => {

      if (this.project) {
        this.project = {
          ...this.project,
          scenes: this.project.scenes.map((scene) => {
            const checkInvalid = (
              !scene.isDualAvatar &&
              scene.clips.filter((clip) => clip.type === "aiModel").length > 1
            ) || (
              scene.isDualAvatar &&
              scene.clips.filter((clip) => clip.type === "aiModel").length > 2
            )
  
            if (!checkInvalid) {
              return scene;
            }

            const validModelIdList = scene.scripts.map((script) => script.clipId);
  
            const checkFixable = scene.clips
              .map((clip) => clip.id)
              .reduce((acc, id) => {
                if (validModelIdList.includes(id)) {
                  return true;
                }
  
                return acc;
              }, false);
  
            if (!checkFixable) {
              return scene;
            }
            
            return {
              ...scene,
              clips: scene.clips.filter((clip) => {
                if (
                  clip.type === "aiModel" &&
                  !validModelIdList.includes(clip.id)
                ) {
                  return false;
                }
  
                return true;
              })
            }
          }),
        };
      }

      return { isSucceed: true };
    }
  };

  [ExportProgramName.normalizeClip]:ExportProgram = {
    do: async () => {
      if (this.project) {
        // 아이디 정규화
        this.project.scenes = normalizeClipId(this.project.scenes);

        // 레이어 정규화
        this.project.scenes = normalizeClipLayer(this.project.scenes);
        
        // 폰트 스타일 정규화
        this.project.scenes = normalizeTextStyle(this.project.scenes);

        this.project.scenes = this.project.scenes.map((scene) => ({
          ...scene,
          clips: scene.clips.map((clip) => migrateClipTrim(clip)),
        }));
      }

      return { isSucceed: true };
    }
  };

  [ExportProgramName.restoreCaptionTime]:ExportProgram = {
    do: async () => {
      if (this.project) {
        this.project.scenes = this.project.scenes.map((scene) => {
          const tempo = Number(scene?.tempo || 1);

          return {
            ...scene,
            clips: scene.clips.map((clip) => {
              if (clip.type === "captions" && clip.captions) {
                return {
                  ...clip,
                  captions: clip.captions.map((caption, index, array) => {
                    const currentStart = caption.start * tempo;
                    const currentEnd = caption.end * tempo;
                    const nextStart = array[index + 1]
                      ? array[index + 1].start * tempo
                      : null;

                    const start = Number(currentStart.toFixed(2));
                    const end = nextStart && nextStart < currentEnd
                      ? Number(nextStart.toFixed(2))
                      : Number(currentEnd.toFixed(2));
                      
                    return { ...caption, start, end }
                  })
                }
              }
              return clip;
            })
          };
        })
      }

      return { isSucceed: true };
    }
  };

  [ExportProgramName.checkInvalidVoice]:ExportProgram = {
    do: async () => {
      if (this.project) {
        const checkedResults = this.project.scenes.map((scene) => {
          return scene.scripts.some((script) => {
            if (script.tts) {
              return false;
            }

            if (!script.clipId) {
              return false;
            }

            const avatarClip = scene.clips.find((clip) => clip.id === script.clipId);

            if (!avatarClip) {
              return true;
            }

            const avatarType = avatarClip.avatarType;

            if (!avatarType || avatarType === "original") {
              return false;
            }
            
            return true;
          });
        });

        const checkedResultIndex = checkedResults.findIndex((result) => result);

        if (checkedResultIndex !== -1) {
          return { isSucceed: false, data: checkedResultIndex }
        }
      }

      return { isSucceed: true };
    }
  };

  [ExportProgramName.exportEarlyStreaming]: ExportProgram = {
    do: async () => {
      if (typeof window === "undefined") {
        const { projectId, userId, workspaceId, db } = this;

        if (!projectId || !userId || !workspaceId || !db) {
          return { isSucceed: false, data: "AS2601" }; // no args
        }

        try {
          const {
            getAutomationStreamingProject,
            getAutomationStreamingEvent,
            getAutomationStreamingCaptions,
            completeAutomationStreaming,
            getAutomationStreamingMediaInfo,
          } = await import("@/lib/automation/automation-streaming-utils");
          
          const { project, job } = await getAutomationStreamingProject({
            db,
            userId,
            projectId,
            workspaceId,
          });

          this.project = project as unknown as ProjectType;

          if (job.status.finish) {
            return { isSucceed: true };
          }

          const initialScenes: ((Omit<Scene, "id"> & { id: string }) | undefined)[] = [];

          const templates = job.templates;

          const messages: AutomationStreamingStream[] = [];

          await getAutomationStreamingEvent({
            db,
            userId,
            projectId,
            onMessage: (message) => {
              messages.push(message);

              const nextScenes = applyStream({
                scenes: initialScenes,
                stream: message,
                templates,
              });
              initialScenes.splice(0, initialScenes.length, ...nextScenes);
            },
            workspaceId,
          });

          const errorEventMessage = messages.find((message) => message.jobStatus === "error");
          
          if (errorEventMessage) {
            throw new ApiError(
              errorEventMessage.errorCode ? String(errorEventMessage.errorCode) : "AS2602", // stream error
              ""
            );
          }

          const streamedScenes = initialScenes
            .filter((scene) => !!scene)
            .map((scene) => {
              return {
                ...scene,
                clips: (
                  scene.clips
                    .map((clip) => {
                      if (clip.type === "image") {
                        delete clip.video_url;
                        delete clip.volume;
                        delete clip.loop;
                      }

                      if (
                        clip.type === "image" && (
                          clip.tag === "image" ||
                          clip.tag === "video"
                        )
                      ) {
                        clip.tag = "image";
                        clip.id = clip.id.replace("videoImage-", "image-");
                      }

                      if (
                        clip.type === "videoImage" && (
                          clip.tag === "image" ||
                          clip.tag === "video"
                        )
                      ) {
                        clip.tag = "video";
                        clip.id = clip.id.replace("image-", "videoImage-");
                      }

                      clip.name = clip.id;

                      return clip;
                    })
                    .sort((a, b) => a.layer - b.layer)
                )
              }
            });
          
          const appliedScenes = await getAutomationStreamingCaptions({
            db,
            userId,
            projectId,
            scenes: streamedScenes,
          });

          const mediaInfoResponse = await getAutomationStreamingMediaInfo({
            db,
            projectId
          });

          const mediaInfo = (() => {
            if (!mediaInfoResponse) {
              return [];
            }

            return (
              appliedScenes.reduce((acc: AutomationStreamingMediaInfo[], scene, index) => {
                const sceneMediaInfoInput = mediaInfoResponse.input[index] || "";

                const sceneMediaInfos = scene.clips
                  .filter((clip) => clip.tag === "image" || clip.tag === "video")
                  .map((clip) => ({
                    clipId: clip.id,
                    sceneId: scene.id || "",
                    mediaResourceType: mediaInfoResponse.mediaResourceType,
                    generativeMediaStyle: mediaInfoResponse.generativeMediaStyle,
                    input: sceneMediaInfoInput,
                  }));

                return [
                  ...acc,
                  ...sceneMediaInfos,
                ]
              }, [])
            );
          })();
          

          await completeAutomationStreaming({
            db,
            userId,
            projectId,
            scenes: appliedScenes,
            workspaceId,
            mediaInfo,
          });

          this.project = { ...project, scenes: appliedScenes } as unknown as ProjectType;

          return { isSucceed: true };
        } catch (e) {
          const errorCode = e instanceof ApiError ? e.code : "AS2603"; // common error
          return { isSucceed: false, data: errorCode };
        }
      } else {
        return { isSucceed: false, data: "AS2604" }; // no client side error
      }
    }
  }
}
